// src/services/AuthService.js
import storage from './StorageAdapter';
import auditService from './AuditService';
import entityService from './EntityService';
import { applyDualWriteMixin, makeDualWriteMixin } from './_dualWriteHelpers';
import authConfigApi from '../api/authConfig';

// Default demo users — seeded on first use when no local users are configured.
// Replace by switching provider to 'jwt' and pointing to a backend endpoint.
const DEFAULT_USERS = [
  { email: 'simon@mertil.se', password: 'mertil2025', role: 'admin', name: 'Simon' }
];

export const SSO_PROVIDERS = ['oidc', 'saml'];

const DEFAULT_OIDC = {
  discoveryUrl: '',
  clientId: '',
  clientSecret: '',
  redirectUri: '',
  scope: 'openid profile email',
};

const DEFAULT_SAML = {
  entityId: '',
  idpMetadataUrl: '',
  acsUrl: '',
  nameIdFormat: 'emailAddress',
};

// ADM-205: backend dual-write för SSO/auth-config.
// State-key (backend): 'config' (single key innehållande hela auth-objektet).
// Tenant-scopas via X-Tenant-Id automatiskt.
const STATE_KEY = 'config';

class AuthService {
  constructor() {
    this.STORAGE_KEY = 'rms_auth_config';
    this.DEFAULTS = {
      provider: 'local',
      jwt: { endpoint: '', clientId: '' },
      oidc: { ...DEFAULT_OIDC },
      saml: { ...DEFAULT_SAML },
    };
  }

  _entityKey(_baseKey) {
    // AuthService:s STORAGE_KEY är inte entity-prefixed — auth-config gäller globalt
    // per tenant. Mixin förväntar sig en _entityKey-metod, så vi mappar baseKey
    // direkt till STORAGE_KEY för dual-write.
    return this.STORAGE_KEY;
  }

  getAuthConfig() {
    const stored = storage.getJSON(this.STORAGE_KEY, {});
    return {
      ...this.DEFAULTS,
      ...stored,
      oidc: { ...DEFAULT_OIDC, ...(stored.oidc || {}) },
      saml: { ...DEFAULT_SAML, ...(stored.saml || {}) },
    };
  }

  async _persistAuthConfig(config) {
    await storage.setJSON(this.STORAGE_KEY, config);
    this._syncStateToBackend(STATE_KEY, config);
  }

  async saveAuthConfig(config) {
    const merged = { ...this.getAuthConfig(), ...config };
    await this._persistAuthConfig(merged);
    auditService.logUpdate('Admin', 'AuthConfig', 'system', {
      provider: config.provider
    }, 'Auth configuration updated');
    return { success: true };
  }

  async saveSSOConfig(provider, config) {
    if (!SSO_PROVIDERS.includes(provider)) {
      return { success: false, error: 'invalid_provider' };
    }
    const current = this.getAuthConfig();
    const updated = { ...current, [provider]: { ...current[provider], ...config } };
    await this._persistAuthConfig(updated);
    auditService.logUpdate('Admin', 'SSOConfig', 'system', { provider }, 'SSO configuration updated');
    return { success: true };
  }

  async enableSSOProvider(provider) {
    if (!SSO_PROVIDERS.includes(provider) && provider !== 'local' && provider !== 'jwt') {
      return { success: false, error: 'invalid_provider' };
    }
    return this.saveAuthConfig({ provider });
  }

  async testSSOConnection(provider, config) {
    if (provider === 'oidc') {
      if (!config.discoveryUrl || !config.clientId) {
        return { success: false, error: 'missing_required_fields' };
      }
      if (!config.discoveryUrl.startsWith('https://')) {
        return { success: false, error: 'invalid_discovery_url' };
      }
      return { success: true, message: 'oidc_connection_ok', metadata: { issuer: config.discoveryUrl } };
    }

    if (provider === 'saml') {
      if (!config.entityId || !config.idpMetadataUrl) {
        return { success: false, error: 'missing_required_fields' };
      }
      if (!config.idpMetadataUrl.startsWith('https://')) {
        return { success: false, error: 'invalid_metadata_url' };
      }
      return { success: true, message: 'saml_connection_ok', metadata: { entityId: config.entityId } };
    }

    return { success: false, error: 'unsupported_provider' };
  }

  getLocalUsers() {
    const config = this.getAuthConfig();
    return config.localUsers || DEFAULT_USERS;
  }

  getProvider() {
    return this.getAuthConfig().provider;
  }

  isJWTConfigured() {
    const config = this.getAuthConfig();
    return config.provider === 'jwt' && Boolean(config.jwt && config.jwt.endpoint);
  }

  isOIDCConfigured() {
    const config = this.getAuthConfig();
    return Boolean(config.oidc?.discoveryUrl && config.oidc?.clientId);
  }

  isSAMLConfigured() {
    const config = this.getAuthConfig();
    return Boolean(config.saml?.entityId && config.saml?.idpMetadataUrl);
  }

  async authenticate(email, password) {
    const config = this.getAuthConfig();

    if (config.provider === 'local') {
      const users = this.getLocalUsers();
      const user = users.find(u => u.email === email && u.password === password);
      if (user) {
        return {
          success: true,
          user: { email: user.email, role: user.role, name: user.name },
          token: null
        };
      }
      return { success: false };
    }

    if (config.provider === 'jwt') {
      return { success: false, error: 'jwt_not_configured' };
    }

    if (config.provider === 'oidc') {
      return { success: false, error: 'sso_redirect_required', provider: 'oidc' };
    }

    if (config.provider === 'saml') {
      return { success: false, error: 'sso_redirect_required', provider: 'saml' };
    }

    return { success: false };
  }

  async setUserPassword(email, password) {
    const config = this.getAuthConfig();
    const users = [...(config.localUsers || DEFAULT_USERS)];
    const idx = users.findIndex(u => u.email === email);
    if (idx >= 0) {
      users[idx] = { ...users[idx], password };
    } else {
      users.push({ email, password, role: 'local', name: email });
    }
    await this._persistAuthConfig({ ...config, localUsers: users });
    return { success: true };
  }

  authenticateWithJWT(token) {
    return Promise.resolve({ success: false, error: 'jwt_not_implemented' });
  }

  notifyListeners() {
    // No-op — AuthService har inga listeners idag, men mixin kan anropa det
  }

  /**
   * ADM-205: pulla auth-config från backend och skriv till localStorage.
   * Kallas från App.js efter login. Backend-state vinner.
   */
  async initFromBackend() {
    return this._initFromBackend([STATE_KEY], []);
  }
}

applyDualWriteMixin(AuthService, makeDualWriteMixin({
  storage,
  api: authConfigApi,
  serviceName: 'AuthService',
}));

const authService = new AuthService();
export default authService;
